/**
 * Deliverables (roadmap step 7; docs/specs/cloud/deliverables.md): the client share links of ADR-008, the processors'
 * zips of R165 (decision 925) and the Safety section of R162 (decision 926).
 *
 * This file has no imports on purpose: the Edge Functions (Deno) use an identical copy,
 * `supabase/functions/_shared/deliverables.ts` (tools/dev/copy-edge-shared.mjs; `test/deliverables.test.ts` fails
 * when the two differ).
 */

// ============================================================================================== share links

/** Where a share link points (ADR-008's providers), worked out from its address. */
export type ShareLinkProvider =
  'nextcloud' | 'google_drive' | 'onedrive' | 'dropbox' | 'sftp' | 'ftp' | 's3' | 'other';

export const SHARE_LINK_PROVIDER_LABEL: Readonly<Record<ShareLinkProvider, string>> = {
  nextcloud: 'Nextcloud',
  google_drive: 'Google Drive',
  onedrive: 'OneDrive or SharePoint',
  dropbox: 'Dropbox',
  sftp: 'SFTP',
  ftp: 'FTP',
  s3: 'S3',
  other: 'Web link',
};

export const SHARE_LINK_LABEL_MAX = 120;
export const SHARE_LINK_URL_MAX = 2000;
export const SHARE_LINK_NOTE_MAX = 500;

const SCHEMES = ['https:', 'sftp:', 'ftp:', 'ftps:', 's3:'] as const;

/** The provider of a share link's address (the address is assumed checked). */
export function shareLinkProvider(address: string): ShareLinkProvider {
  let u: URL;
  try {
    u = new URL(address.trim());
  } catch {
    return 'other';
  }
  const host = u.hostname.toLowerCase();
  if (u.protocol === 'sftp:') return 'sftp';
  if (u.protocol === 'ftp:' || u.protocol === 'ftps:') return 'ftp';
  if (u.protocol === 's3:') return 's3';
  const is = (h: string): boolean => host === h || host.endsWith(`.${h}`);
  if (is('drive.google.com') || is('docs.google.com')) return 'google_drive';
  if (is('1drv.ms') || is('sharepoint.com') || is('onedrive.live.com')) return 'onedrive';
  if (is('dropbox.com') || is('db.tt')) return 'dropbox';
  if (is('amazonaws.com')) return 's3';
  // a Nextcloud public share: /s/<token> or /index.php/s/<token>
  if (/^\/(index\.php\/)?s\/[A-Za-z0-9]{6,}\/?$/.test(u.pathname)) return 'nextcloud';
  return 'other';
}

export interface ShareLinkInput {
  readonly label: string;
  readonly url: string;
  /** yyyy-mm-dd, or '' / null for no expiry. */
  readonly expiresOn?: string | null;
  readonly note?: string | null;
}

/** What is wrong with a share link before it is added, in words (none: it can be added). The database checks too. */
export function shareLinkProblems(l: ShareLinkInput, today: string): string[] {
  const out: string[] = [];
  const label = l.label.trim();
  if (!label) out.push('Give the link a label, such as "Final data, block Milgun".');
  else if (label.length > SHARE_LINK_LABEL_MAX)
    out.push(`Shorten the label to ${SHARE_LINK_LABEL_MAX} characters.`);
  const address = l.url.trim();
  let u: URL | null = null;
  try {
    u = new URL(address);
  } catch {
    u = null;
  }
  if (!address) out.push('Paste the share link’s address.');
  else if (address.length > SHARE_LINK_URL_MAX)
    out.push(`The address is longer than ${SHARE_LINK_URL_MAX} characters.`);
  else if (!u || !(SCHEMES as readonly string[]).includes(u.protocol) || !u.hostname)
    out.push('The address must start with https://, sftp://, ftp://, ftps:// or s3://.');
  else if (u.username || u.password)
    out.push('Put the password in the share’s own settings, not in the address.');
  const exp = (l.expiresOn ?? '').trim();
  if (exp && (!/^\d{4}-\d{2}-\d{2}$/.test(exp) || Number.isNaN(Date.parse(`${exp}T00:00:00Z`))))
    out.push('The expiry must be a date.');
  else if (exp && exp < today) out.push('The expiry date has passed already.');
  if ((l.note ?? '').length > SHARE_LINK_NOTE_MAX)
    out.push(`Shorten the note to ${SHARE_LINK_NOTE_MAX} characters.`);
  return out;
}

export type ShareLinkState = 'works' | 'expired' | 'withdrawn';

/** A link's state now: withdrawn by staff, past its expiry (the end of that day, UTC+0), or working. */
export function shareLinkState(
  l: { readonly withdrawn_at?: string | null; readonly expires_on?: string | null },
  now: Date,
): ShareLinkState {
  if (l.withdrawn_at) return 'withdrawn';
  if (l.expires_on && now.toISOString().slice(0, 10) > l.expires_on) return 'expired';
  return 'works';
}

export const SHARE_LINK_STATE_LABEL: Readonly<Record<ShareLinkState, string>> = {
  works: 'Works',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** `30 Nov 2026` for `2026-11-30`. */
export function shortDate(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return day;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

/** `28 October 2026` for `2026-10-28`. */
export function deliverableLongDate(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return day;
  return `${Number(m[3])} ${MONTHS_LONG[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

/** The line under a link: "Works until 30 Nov 2026", "Expired on 1 Nov 2026", "Withdrawn". */
export function shareLinkStateText(
  l: { readonly withdrawn_at?: string | null; readonly expires_on?: string | null },
  now: Date,
): string {
  const s = shareLinkState(l, now);
  if (s === 'withdrawn') return 'Withdrawn: the client can no longer open it.';
  if (s === 'expired') return `Expired on ${shortDate(l.expires_on ?? '')}.`;
  return l.expires_on ? `Works until ${shortDate(l.expires_on)}.` : 'Works, with no expiry date.';
}

// ============================================================================================== processors' zips

/** The rego as it is in a zip name: without its country prefix (`VH-JOZ` → `JOZ`, `P2-PHU` → `PHU`). */
export function regoShort(rego: string | null | undefined): string {
  const r = (rego ?? '').trim().toUpperCase();
  const dash = r.indexOf('-');
  const tail = dash >= 0 ? r.slice(dash + 1) : r.startsWith('VH') && r.length === 5 ? r.slice(2) : r;
  return tail.replace(/[^A-Z0-9]/g, '') || 'NOREGO';
}

export interface ProcessorZipNameInput {
  readonly projectNumber: string;
  readonly rego: string | null | undefined;
  readonly jday: number;
  readonly flightNumber: number;
}

const pad3 = (n: number): string => String(Math.trunc(n)).padStart(3, '0');

/** `F26048_JOZ_301_FLT029` (decision 925: Project_aircraft rego_julian day_flight number), without `.zip`. */
export function processorZipStem(i: ProcessorZipNameInput): string {
  const project = i.projectNumber.trim().replace(/[^A-Za-z0-9-]/g, '') || 'PROJECT';
  return `${project}_${regoShort(i.rego)}_${pad3(i.jday)}_FLT${pad3(i.flightNumber)}`;
}

/** `F26048_JOZ_301_FLT029.zip`. */
export function processorZipName(i: ProcessorZipNameInput): string {
  return `${processorZipStem(i)}.zip`;
}

/** Characters Nextcloud (and Windows) refuse in a folder name. */
const BAD_SEGMENT = /[\\/:*?"<>|\u0000-\u001f]/g; // eslint-disable-line no-control-regex

/**
 * The project folder of the processors' zips: the last part of the laptop's project folder (decision 925:
 * `C:\TA_Survey\F26048_Benz_Mining_Corp_Milgun_WA` becomes `F26048_Benz_Mining_Corp_Milgun_WA`), cleaned for
 * Nextcloud; the project number when the folder is unknown.
 */
export function processorProjectFolder(folderPath: string | null | undefined, projectNumber: string): string {
  const last = (folderPath ?? '').replace(/[\\/]+$/, '').replace(/^.*[\\/]/, '');
  const clean = last.replace(BAD_SEGMENT, '').trim().replace(/^\.+$/, '').slice(0, 200);
  if (clean) return clean;
  const n = projectNumber.replace(BAD_SEGMENT, '').trim();
  return n || 'Project';
}

/** The tenant's processors' folder when none is set: `<archive folder>/Field Data` (decision 925). */
export const PROCESSOR_FOLDER_DEFAULT_NAME = 'Field Data';

/** The processors' folder in the Nextcloud account's files: the one set, else `<archive folder>/Field Data`. */
export function processorFolder(
  set: string | null | undefined,
  archiveFolder: string | null | undefined,
): string {
  const tidy = (f: string): string =>
    `/${f
      .trim()
      .replace(/\\/g, '/')
      .replace(/^\/+|\/+$/g, '')}`;
  if (set && set.trim().replace(/[\\/]/g, '')) return tidy(set);
  const base = archiveFolder && archiveFolder.trim().replace(/[\\/]/g, '') ? tidy(archiveFolder) : '';
  return `${base}/${PROCESSOR_FOLDER_DEFAULT_NAME}`;
}

/** Whether a processors' folder a tenant admin types is one (a path in the account's files, no `..`). */
export function processorFolderProblem(folder: string): string | null {
  const f = folder.trim();
  if (!f) return null; // unset: the default
  if (f.length > 500) return 'Shorten the folder to 500 characters.';
  const parts = f.replace(/\\/g, '/').split('/').filter(Boolean);
  if (parts.length === 0) return 'Name a folder, such as /Shared/Field Data.';
  if (parts.some((p) => p === '.' || p === '..')) return 'The folder cannot contain "." or ".." parts.';
  // eslint-disable-next-line no-control-regex
  if (parts.some((p) => /[:*?"<>|\u0000-\u001f]/.test(p)))
    return 'The folder name has a character Nextcloud does not take (: * ? " < > |).';
  return null;
}

/** A flight XYZ (`029064121.XYZ`): not the untrimmed `_080000` file, not `_Diurnal`, not a basemag export. */
export function isFlightXyzName(name: string): boolean {
  // as crates/teslyn-core flight_xyz.rs is_flight_xyz_name: a stem of at least nine digits
  return /^\d{9,}\.xyz$/i.test(name);
}

export interface ZipEntry {
  /** The file on the laptop. */
  readonly path: string;
  /** Its name inside the zip's folder (as on disk). */
  readonly name: string;
}

const base = (p: string): string => p.replace(/^.*[\\/]/, '');
const join = (folder: string, name: string): string =>
  `${folder.replace(/[\\/]+$/, '')}${folder.includes('/') && !folder.includes('\\') ? '/' : '\\'}${name}`;

export interface FlightFolderListing {
  /** The folder (a flight's `Flt029`). */
  readonly folder: string;
  /** The file names in it (not sub-folders). */
  readonly names: readonly string[];
}

export interface ProcessorFlightFiles {
  readonly log: ZipEntry | null;
  readonly xyz: readonly ZipEntry[];
  readonly spo: readonly ZipEntry[];
  readonly diurnal: readonly ZipEntry[];
}

/**
 * A flight's files for the processors (decision 925): its `.LOG`, each flight `.XYZ` in its folders, and the `.SPO`
 * and `_Diurnal.XYZ` of the same stem. Names are kept as on disk (decision 459). The log is the flight's own
 * (`flight.log_path`) when it is in a listed folder, else the folder's `FLT<nnn>.LOG`.
 */
export function processorFlightFiles(
  logPath: string | null | undefined,
  flightNumber: number,
  folders: readonly FlightFolderListing[],
): ProcessorFlightFiles {
  const xyz: ZipEntry[] = [];
  const spo: ZipEntry[] = [];
  const diurnal: ZipEntry[] = [];
  let log: ZipEntry | null = null;
  const logName = logPath ? base(logPath).toLowerCase() : `flt${pad3(flightNumber)}.log`;
  for (const f of folders) {
    const names = [...f.names].sort();
    const lower = new Map(names.map((n) => [n.toLowerCase(), n]));
    if (!log && lower.has(logName))
      log = { path: join(f.folder, lower.get(logName)!), name: lower.get(logName)! };
    for (const n of names.filter(isFlightXyzName)) {
      xyz.push({ path: join(f.folder, n), name: n });
      const stem = n.slice(0, -4).toLowerCase();
      const s = lower.get(`${stem}.spo`);
      if (s) spo.push({ path: join(f.folder, s), name: s });
      const d = lower.get(`${stem}_diurnal.xyz`);
      if (d) diurnal.push({ path: join(f.folder, d), name: d });
    }
  }
  return { log, xyz, spo, diurnal };
}

export interface BasemagCandidate {
  readonly path: string;
  /** When it was written (ISO), newest wins; '' when unknown. */
  readonly at?: string;
}

/**
 * The day's basemag `.raw` and `.xyz` for the processors: the newest pair named for that day
 * (`F26048_D301_Mag_Station.raw` and `.xyz`, `D301_<station>.raw` …, Diurnal QC's `dataFileNames`), from Teslyn's own
 * exports first, else the day folder's `1. Basemags`. Null unless both are there. Teslyn never writes these files
 * from readings with observatory data spliced in (decision 458), and writes the stored readings unedited (decisions
 * 22, 23), so a pair found here may go to the processors.
 */
export function processorBasemagFiles(
  jday: number,
  exported: readonly BasemagCandidate[],
  dayFolder: readonly BasemagCandidate[],
): { readonly raw: ZipEntry; readonly xyz: ZipEntry } | null {
  const tag = new RegExp(`(^|_)D${pad3(jday)}_[^\\\\/]*\\.(raw|xyz)$`, 'i');
  const pick = (list: readonly BasemagCandidate[]) => {
    const of = (ext: string) =>
      [...list]
        .filter((c) => tag.test(base(c.path)) && base(c.path).toLowerCase().endsWith(`.${ext}`))
        .sort(
          (a, b) => (b.at ?? '').localeCompare(a.at ?? '') || base(a.path).localeCompare(base(b.path)),
        )[0];
    const raw = of('raw');
    if (!raw) return null;
    // the .xyz of the same name as the .raw (the export writes both)
    const stem = base(raw.path).slice(0, -4).toLowerCase();
    const xyz = list.find((c) => base(c.path).toLowerCase() === `${stem}.xyz`) ?? of('xyz');
    if (!xyz) return null;
    return {
      raw: { path: raw.path, name: base(raw.path) },
      xyz: { path: xyz.path, name: base(xyz.path) },
    };
  };
  return pick(exported) ?? pick(dayFolder);
}

/** Why a flight's zip cannot be made yet (shown on the Reports screen), or why it is not this laptop's to make. */
export type ProcessorZipWait = 'files_elsewhere' | 'no_xyz' | 'no_basemag';

export function processorZipWaitText(why: ProcessorZipWait, jday: number): string {
  switch (why) {
    case 'files_elsewhere':
      return 'The flight’s files are not on this laptop: the laptop that imported the flight sends its zip.';
    case 'no_xyz':
      return 'The flight’s XYZ is not in its folder yet: export it (GED2XYZ), then sync.';
    case 'no_basemag':
      return `Export day ${pad3(jday)}’s basemag .raw and .xyz in Diurnal QC (Basemag files) so the processors get them; the zip goes at the next sync after that.`;
  }
}

export type ProcessorZipPlan =
  | { readonly state: 'ready'; readonly entries: readonly ZipEntry[] }
  | { readonly state: 'waiting'; readonly why: ProcessorZipWait };

/**
 * What goes into one flight's zip, in the order MB's own zips hold them (by name), or why it waits. A missing `.SPO`
 * or `_Diurnal.XYZ` does not stop the zip (its record lists what it holds); no log or XYZ means the files are on
 * another laptop or not exported yet; no basemag export means it waits (spec §5.2, question 2).
 */
export function processorZipPlan(
  flight: ProcessorFlightFiles,
  basemag: { readonly raw: ZipEntry; readonly xyz: ZipEntry } | null,
): ProcessorZipPlan {
  if (!flight.log) return { state: 'waiting', why: 'files_elsewhere' };
  if (flight.xyz.length === 0) return { state: 'waiting', why: 'no_xyz' };
  if (!basemag) return { state: 'waiting', why: 'no_basemag' };
  const all = [flight.log, ...flight.xyz, ...flight.spo, ...flight.diurnal, basemag.raw, basemag.xyz];
  const seen = new Set<string>();
  const entries = all
    .filter((e) => {
      const k = e.name.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  return { state: 'ready', entries };
}

/** Bytes as the email and the page say them: `6.9 MB`, `340 KB`. */
export function sizeText(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

export interface ProcessorZipEmailFacts {
  readonly projectNumber: string;
  readonly zipName: string;
  readonly sizeBytes: number;
  readonly jday: number;
  readonly flightNumber: number;
  /** yyyy-mm-dd: the daily report's day. */
  readonly day: string;
  readonly reportRevision: number;
  /** The zip's earlier revision, when this one replaces it. */
  readonly replacesRevision: number | null;
  /** 'nextcloud' or 'survera' (Teslyn Cloud storage). */
  readonly destination: 'nextcloud' | 'survera';
  /** The path shown: the Nextcloud path, or the zip's place in Teslyn Cloud. */
  readonly where: string;
  readonly contents: readonly string[];
}

const listWords = (xs: readonly string[]): string =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

/** The data processors' email when a zip is ready (decision 925: the recommendation, email on, stood). */
export function processorZipEmail(f: ProcessorZipEmailFacts): {
  readonly subject: string;
  readonly body: string;
} {
  const subject = `${f.projectNumber} day ${pad3(f.jday)} FLT${pad3(f.flightNumber)}: data ready for processing`;
  const where =
    f.destination === 'nextcloud'
      ? `is in Nextcloud: ${f.where}`
      : 'is in Teslyn Cloud: open Deliverables, choose the project, and download it under "For the processors"';
  const lines = [
    `${f.zipName} (${sizeText(f.sizeBytes)}) ${where}`,
    '',
    `It holds ${listWords(f.contents)}, sent with the daily report of ${deliverableLongDate(f.day)} (revision ${f.reportRevision}).`,
  ];
  if (f.replacesRevision !== null)
    lines.push('', `It replaces the zip sent with revision ${f.replacesRevision}.`);
  return { subject: subject.slice(0, 150), body: lines.join('\n').slice(0, 5000) };
}

/** A daily report as Teslyn Cloud publishes it (report_publication with the report's day and revision). */
export interface PublishedDailyReport {
  readonly report_id: string;
  /** The report's day (yyyy-mm-dd). */
  readonly period: string;
  readonly revision: number;
  readonly published: boolean;
}

/** A flight's zip as the laptop holds it (teslyn-db processor_zip). */
export interface ProcessorZipHeld {
  readonly id: string;
  readonly report_revision: number;
  readonly state: 'waiting' | 'pending' | 'done' | 'refused' | 'missing';
}

/**
 * The days whose zips are due (decision 925): for each day with a daily report published to the client, its newest
 * published revision. A flight's zip of that day is made, or made again, when the laptop has none, has one of an
 * older revision ("a newer revision replaces the zip"), or has one waiting or gone missing; a zip pending, sent or
 * refused for that revision is left as it is.
 */
export function processorZipDays(published: readonly PublishedDailyReport[]): PublishedDailyReport[] {
  const newest = new Map<string, PublishedDailyReport>();
  for (const r of published) {
    if (!r.published || !/^\d{4}-\d{2}-\d{2}$/.test(r.period)) continue;
    const had = newest.get(r.period);
    if (!had || r.revision > had.revision) newest.set(r.period, r);
  }
  return [...newest.values()].sort((a, b) => a.period.localeCompare(b.period));
}

/** Whether a flight's zip must be made (again) for a published revision of its day. */
export function processorZipNeeded(held: ProcessorZipHeld | null | undefined, revision: number): boolean {
  if (!held) return true;
  if (held.report_revision < revision) return true;
  if (held.report_revision > revision) return false;
  return held.state === 'waiting' || held.state === 'missing';
}

// ============================================================================================== Safety (R162)

export type SafetyDocumentKind = 'risk_assessment' | 'jsa' | 'jsp' | 'hazards_kml';

export const SAFETY_DOCUMENT_KINDS: readonly SafetyDocumentKind[] = [
  'risk_assessment',
  'jsa',
  'jsp',
  'hazards_kml',
];

export const SAFETY_DOCUMENT_LABEL: Readonly<Record<SafetyDocumentKind, string>> = {
  risk_assessment: 'Risk assessment',
  jsa: 'Signed JSA',
  jsp: 'Signed JSP',
  hazards_kml: 'Hazards KML',
};

/** The files each kind takes (decision 926: the risk assessment stays the company's .xlsm, as is). */
export const SAFETY_FILE_TYPES: Readonly<
  Record<Exclude<SafetyDocumentKind, 'hazards_kml'>, readonly string[]>
> = {
  risk_assessment: ['.xlsm'],
  jsa: ['.pdf'],
  jsp: ['.pdf'],
};

export const SAFETY_CONTENT_TYPE: Readonly<Record<string, string>> = {
  '.xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
  '.pdf': 'application/pdf',
};

/** The `safety` bucket's limit. */
export const SAFETY_MAX_BYTES = 50 * 1024 * 1024;

/** What is wrong with a file for a kind, in words, or null. */
export function safetyFileProblem(
  kind: Exclude<SafetyDocumentKind, 'hazards_kml'>,
  fileName: string,
  sizeBytes: number,
): string | null {
  const name = fileName.trim();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
  const allowed = SAFETY_FILE_TYPES[kind];
  if (!allowed.includes(ext)) {
    return kind === 'risk_assessment'
      ? 'The risk assessment is added as the company’s Excel macro file (.xlsm), as it is.'
      : `The ${SAFETY_DOCUMENT_LABEL[kind].replace('Signed ', '')} is added as its signed PDF.`;
  }
  if (sizeBytes <= 0) return 'The file is empty.';
  if (sizeBytes > SAFETY_MAX_BYTES)
    return `The file is ${sizeText(sizeBytes)}; Teslyn Cloud takes at most 50 MB.`;
  if (name.length > 200) return 'Shorten the file name to 200 characters.';
  return null;
}

/** The content type stored for a file name. */
export function safetyContentType(fileName: string): string {
  const ext = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.')).toLowerCase() : '';
  return SAFETY_CONTENT_TYPE[ext] ?? 'application/octet-stream';
}

export interface SafetyDocumentRow {
  readonly id: string;
  readonly kind: SafetyDocumentKind;
  readonly version: number;
  readonly shown: boolean;
  readonly removed_at?: string | null;
}

/**
 * What the client sees of a project's Safety documents: per kind, the newest version shown (decision 926: "a newer
 * version replaces the old one"). The database keeps the same (a newer version added to a shown kind is shown in
 * its place, safety_document_newer_version); this is the reader's view of it.
 */
export function clientSafetyDocuments<T extends SafetyDocumentRow>(rows: readonly T[]): T[] {
  const out: T[] = [];
  for (const kind of SAFETY_DOCUMENT_KINDS) {
    const shown = rows
      .filter((r) => r.kind === kind && r.shown && !r.removed_at)
      .sort((a, b) => b.version - a.version)[0];
    if (shown) out.push(shown);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- the client's KML

/** The pack's top folder of landholder properties (Teslyn Field's build_pack: "Pinned Properties (n)"). */
const PROPERTIES_FOLDER = /<Folder>\s*<name>\s*Pinned Properties \(\d+\)\s*<\/name>/g;

/** The end of the `<Folder>` that opens at `from` (its matching `</Folder>`), or -1. */
function folderEnd(kml: string, from: number): number {
  const tag = /<(\/?)Folder\b[^>]*>/g;
  tag.lastIndex = from;
  let depth = 0;
  for (let m = tag.exec(kml); m; m = tag.exec(kml)) {
    depth += m[1] ? -1 : 1;
    if (depth === 0) return m.index + m[0].length;
  }
  return -1;
}

/**
 * The hazards KML as the client may have it (R162, decision 926; decision 83): the pilot hazard pack, with its
 * landholder properties left out unless the landholder boundaries are shared for the project, and then only their
 * names and shapes (every description inside the properties removed: contacts, notes and flags are internal).
 * Everything else is the pack the pilots load. Null when the KML has no `<Document>` (not a pack).
 */
export function clientHazardKml(kml: string, opts: { readonly landholders: boolean }): string | null {
  if (!/<Document\b/.test(kml)) return null;
  let out = '';
  let at = 0;
  PROPERTIES_FOLDER.lastIndex = 0;
  for (let m = PROPERTIES_FOLDER.exec(kml); m; m = PROPERTIES_FOLDER.exec(kml)) {
    const end = folderEnd(kml, m.index);
    if (end < 0) return null;
    out += kml.slice(at, m.index);
    if (opts.landholders) {
      out += kml.slice(m.index, end).replace(/<description>[\s\S]*?<\/description>/g, '');
    }
    at = end;
    PROPERTIES_FOLDER.lastIndex = end;
  }
  out += kml.slice(at);
  // a line left empty where the folder was
  return out.replace(/\n[ \t]*\n/g, '\n');
}

/** The client's file name of the hazards KML: the pack's own (`F26048_Hazards.kml`). */
export function clientHazardKmlName(projectNumber: string): string {
  return `${projectNumber.trim().replace(/[^A-Za-z0-9_-]/g, '') || 'Project'}_Hazards.kml`;
}
